import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import {
    fetchLocationData,
    fetchCategoryData,
    fetchSubcategoryData,
    fetchPeopleData,
    fetchSlotData,
    bookAppointment,
    fetchSettingData
} from '../api/appointmentsApi';

import './Stepper.css';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useGeneralSettings } from '../../../contexts/GeneralSettingsContext';
import { formatDateTimeCustom, formatDynamicDateOnly } from '../../../helper-functions/DateFunction';
// import { format, parse, isBefore, startOfToday, addDays, startOfWeek, endOfWeek, eachMinuteOfInterval } from 'date-fns';
import { format, parse, isBefore, startOfToday, addDays, isSameDay, startOfWeek, endOfWeek, subMinutes, eachMinuteOfInterval, isSameWeek, parseISO } from 'date-fns';

import { fromZonedTime } from 'date-fns-tz';
import moment from 'moment-timezone';
import TimeZoneLabel from './TimeZoneLabel';
const defaultUserImage = '/media/avatars/blank.png'
const steps = {
    1: 'Mode & Location',
    2: 'Select People',
    3: 'Date & Time',
    4: 'Personal Details',
    5: 'Payment',
    6: 'Success'
};
// Aik chota helper function jo "9.5" jaise format ko "09:30" mein badalta hai
const formatTimeToHHMM = (timeString) => {
    const time = parseFloat(timeString);
    if (isNaN(time)) return "00:00"; // Fallback

    const hours = Math.floor(time);
    const minutes = Math.round((time % 1) * 60);

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
}

// Helper function to parse "9:00 AM" format to 24-hour format "09:00"
const parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return null;
    
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) {
        hours += 12;
    } else if (period === 'AM' && hours === 12) {
        hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

const AppointmentStepper = () => {
    const { getSettingValue } = useGeneralSettings();
    const [currentStep, setCurrentStep] = useState(1);

    const [appointmentType, setAppointmentType] = useState(null);
    const [location, setLocation] = useState(null);
    const [category, setCategory] = useState(null);
    const [subcategory, setSubcategory] = useState(null);

    const [locationOptions, setLocationOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);

    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [isSubcategoryLoading, setIsSubcategoryLoading] = useState(false);
    const [isPeopleLoading, setIsPeopleLoading] = useState(false);
    const [error, setError] = useState('');

    const [peopleData, setPeopleData] = useState([]);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isSlotsLoading, setIsSlotsLoading] = useState(false);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [weeklySchedule, setWeeklySchedule] = useState(null);
    const [selectedDateTime, setSelectedDateTime] = useState(null);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [personalDetails, setPersonalDetails] = useState({
        fullName: '',
        email: '',
        mobile: '',
        message: ''
    });
    const [isBooking, setIsBooking] = useState(false);
    const [isPrevDisabled, setIsPrevDisabled] = useState(true);
    const [addonSettings, setAddonSettings] = useState(null);
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    // const [viewRange, setViewRange] = useState({
    //     start: startOfWeek(new Date()),
    //     end: endOfWeek(new Date())
    // });
    const [viewRange, setViewRange] = useState({
        start: startOfWeek(new Date(), { weekStartsOn: 1 }),
        end: endOfWeek(new Date(), { weekStartsOn: 1 })
    });
    const appointmentTypeOptions = [
        { value: 0, label: 'Meeting at Office' },
        { value: 1, label: 'Virtual' }
    ];

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await fetchSettingData();
                setAddonSettings(settings);
            } catch (err) {
                setError("Failed to load required settings for the component.");
            } finally {
                setIsLoadingSettings(false);
            }
        };
        loadSettings();
    }, []); // Khali array ka matlab hai ke yeh sirf mount par chalega
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await fetchCategoryData();
                setCategoryOptions(data);
            } catch (err) {
                setError('Failed to load categories.');
            }
        };
        loadCategories();
    }, []);


    useEffect(() => {
        if (appointmentType === null) return;

        const loadLocations = async () => {
            setIsLocationLoading(true);
            setError('');
            try {
                const data = await fetchLocationData(appointmentType.value);
                setLocationOptions(data);
            } catch (err) {
                setError('Failed to load locations.');
                setLocationOptions([]);
            } finally {
                setIsLocationLoading(false);
            }
        };

        loadLocations();
        setLocation(null);
    }, [appointmentType]);



    useEffect(() => {
        if (!category) return;

        const loadSubcategories = async () => {
            setIsSubcategoryLoading(true);
            setError('');
            try {
                const data = await fetchSubcategoryData(category.value);
                setSubcategoryOptions(data);
            } catch (err) {
                setError('Failed to load sub-categories.');
                setSubcategoryOptions([]);
            } finally {
                setIsSubcategoryLoading(false);
            }
        };

        loadSubcategories();
        setSubcategory(null);
    }, [category]);



    useEffect(() => {
        if (currentStep === 2 && location && subcategory) {
            const loadPeople = async () => {
                setIsPeopleLoading(true);
                setError('');
                try {
                    const data = await fetchPeopleData(location.value, subcategory.value);
                    setPeopleData(data);
                } catch (err) {
                    setError('Failed to load people data.');
                    setPeopleData([]);
                } finally {
                    setIsPeopleLoading(false);
                }
            };
            loadPeople();
        }
    }, [currentStep, location, subcategory]);
    // Comment for future use.
    //  useEffect(() => {
    //         if (currentStep === 3 && selectedPerson) {
    //             const loadSlots = async () => {
    //                 setIsSlotsLoading(true);
    //                 setError('');
    //                 setSelectedDateTime(null); // Step mein enter hote hi selection reset karein
    //                 try {
    //                     const data = await fetchSlotData(selectedPerson.id);
    //                     setBookedSlots(data);
    //                 } catch (err) {
    //                     setError('Failed to load available time slots.');
    //                     setBookedSlots([]);
    //                 } finally {
    //                     setIsSlotsLoading(false);
    //                 }
    //             };
    //             loadSlots();
    //         }
    //     }, [currentStep, selectedPerson]);


    useEffect(() => {
        if (currentStep === 3 && selectedPerson) {
            const loadSlots = async () => {
                setIsSlotsLoading(true);
                setError('');
                setSelectedDateTime(null);
                try {
                    const data = await fetchSlotData(selectedPerson.id);
                    // Parse the new response structure
                    setBookedSlots(data.appointment_date_time || []);
                    setHolidays(data.holidays || []);
                    setWeeklySchedule(data.schedule || null);
                } catch (err) {
                    setError('Failed to load available time slots.');
                    setBookedSlots([]);
                    setHolidays([]);
                    setWeeklySchedule(null);
                } finally {
                    setIsSlotsLoading(false);
                }
            };
            loadSlots();
        }
    }, [currentStep, selectedPerson]);

    // Auto-select next available date if current selected date is a holiday or disabled day
    useEffect(() => {
        if (!weeklySchedule || !selectedDate) return;

        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const dayName = format(selectedDate, 'EEEE').toLowerCase();
        const daySchedule = weeklySchedule[dayName];

        // Check if selected date is a holiday or disabled day
        const isHoliday = holidays && holidays.includes(dateStr);
        const isDisabled = !daySchedule || daySchedule.enabled === false;

        if (isHoliday || isDisabled) {
            // Find next available date (max 30 days ahead)
            let nextDate = new Date(selectedDate);
            let found = false;
            
            for (let i = 1; i <= 30; i++) {
                nextDate = addDays(selectedDate, i);
                const nextDateStr = format(nextDate, 'yyyy-MM-dd');
                const nextDayName = format(nextDate, 'EEEE').toLowerCase();
                const nextDaySchedule = weeklySchedule[nextDayName];
                
                const isNextHoliday = holidays && holidays.includes(nextDateStr);
                const isNextDisabled = !nextDaySchedule || nextDaySchedule.enabled === false;
                
                if (!isNextHoliday && !isNextDisabled) {
                    found = true;
                    break;
                }
            }
            
            if (found) {
                setSelectedDate(nextDate);
                // Don't clear selected slots - allow multi-day selection
            }
        }
    }, [weeklySchedule, holidays, selectedDate]);

    const slotDifference = Number(addonSettings?.slot_difference) || 30;
    const startTime = formatTimeToHHMM(addonSettings?.shift_start_time || '9.0');
    const endTime = formatTimeToHHMM(addonSettings?.shift_end_time || '17.0');
    const userTimeFormat = getSettingValue('time_format') || '12hr';
    const timeZone = getSettingValue('time_zone') || 'UTC';
    const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
    // --- CALENDAR EVENTS GENERATION (CORRECTED LOGIC) ---
    // Comment for future use.
    // const calendarEvents = useMemo(() => {
    //     if (!viewRange.start || !addonSettings) return []; // Guard clause ko behtar banayein

    //     const allEvents = [];
    //     const now = new Date();


    //     const interval = {
    //         start: viewRange.start,
    //         end: viewRange.end
    //     };


    //     for (let day = 0; day < 7; day++) {
    //         const currentDay = addDays(interval.start, day);
    //         // const dayStart = new Date(currentDay.setHours(9, 0, 0, 0));
    //         // const dayEnd = new Date(currentDay.setHours(16, 30, 0, 0)); // 4:30 PM is the last start time

    //         // const dailySlots = eachMinuteOfInterval(
    //         //     { start: dayStart, end: dayEnd },
    //         //     { step: 30 }
    //         // );
    //         const dayStart = new Date(currentDay.setHours(parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1]), 0, 0));
    //         const dayEnd = new Date(currentDay.setHours(parseInt(endTime.split(':')[0]) - (slotDifference / 60), parseInt(endTime.split(':')[1]), 0, 0)); // Adjust end time based on slot
    //         const dailySlots = eachMinuteOfInterval(
    //             { start: dayStart, end: dayEnd },
    //             { step: slotDifference } // Dynamic step
    //         );

    //         dailySlots.forEach(slotStartTime => {
    //             const isPast = isBefore(slotStartTime, now);
    //             const isBooked = bookedSlots.some(slotString =>
    //                 parse(slotString, 'yyyy-MM-dd h:mm a', new Date()).getTime() === slotStartTime.getTime()
    //             );
    //             const isSelected = selectedSlots.some(slot => slot.getTime() === slotStartTime.getTime());

    //             let eventClassName = 'available-slot';
    //             let eventTitle = '';

    //             if (isPast) {
    //                 eventClassName = 'past-slot';
    //                 eventTitle = 'Past';
    //             } else if (isBooked) {
    //                 eventClassName = 'booked-slot';
    //                 eventTitle = 'Booked';
    //             }
    //             else if (isSelected) {
    //                 eventClassName = 'selected-slot';
    //                 // Selected slot ke liye time dikhana behtar hai
    //                 eventTitle = format(slotStartTime, 'h:mm');
    //             }
    //             // else if (isSelected) {
    //             //     eventClassName = 'selected-slot';
    //             //     eventTitle = 'Selected';
    //             // }

    //             allEvents.push({
    //                 id: slotStartTime.toISOString(), // Unique ID for each slot
    //                 start: slotStartTime,
    //                 // end: new Date(slotStartTime.getTime() + 30 * 60 * 1000),
    //                 end: new Date(slotStartTime.getTime() + slotDifference * 60 * 1000), // Dynamic 
    //                 title: eventTitle,
    //                 className: eventClassName,
    //                 // Custom property to mark as clickable or not
    //                 extendedProps: {
    //                     isClickable: !isPast && !isBooked
    //                 }
    //             });
    //         });
    //     }

    //     return allEvents;
    // }, [bookedSlots, selectedSlots, viewRange, addonSettings]);



    // This is the new handler for when a user clicks a date in the month view
    const handleDateClick = (clickInfo) => {
        const clickedDate = clickInfo.date;

        // Prevent selection of past dates
        if (isBefore(clickedDate, startOfToday())) {
            return;
        }

        // Check if the clicked date is a holiday
        const dateStr = format(clickedDate, 'yyyy-MM-dd');
        if (holidays.includes(dateStr)) {
            // User clicked on a holiday - don't allow selection
            return;
        }

        // Check if the day is enabled in weekly schedule
        if (weeklySchedule) {
            const dayName = format(clickedDate, 'EEEE').toLowerCase(); // 'monday', 'tuesday', etc.
            const daySchedule = weeklySchedule[dayName];
            // Check if the day exists and is enabled
            if (!daySchedule || daySchedule.enabled === false) {
                // This day is not enabled in the schedule
                return;
            }
        }

        // Update the state with the newly selected date
        setSelectedDate(clickedDate);
        // Don't reset selected slots - allow multi-day selection
    };
    const calendarEvents = useMemo(() => {
        if (!selectedDate || !addonSettings) return [];

        // Check if the selected date is a holiday
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        if (holidays && holidays.length > 0 && holidays.includes(dateStr)) {
            // Selected date is a holiday, return empty slots
            return [];
        }

        // Get the day name for weekly schedule check
        const dayName = format(selectedDate, 'EEEE').toLowerCase(); // 'monday', 'tuesday', etc.
        
        // Check if this day is enabled in the schedule
        if (weeklySchedule) {
            const daySchedule = weeklySchedule[dayName];
            if (!daySchedule || daySchedule.enabled === false) {
                // Day is not enabled, return empty slots
                return [];
            }
        }

        // Determine start and end times - use schedule if available, otherwise use default
        let effectiveStartTime = startTime;
        let effectiveEndTime = endTime;

        if (weeklySchedule && weeklySchedule[dayName]) {
            const daySchedule = weeklySchedule[dayName];
            if (daySchedule.enabled && daySchedule.slots && daySchedule.slots.from && daySchedule.slots.to) {
                effectiveStartTime = parseTimeString(daySchedule.slots.from) || startTime;
                effectiveEndTime = parseTimeString(daySchedule.slots.to) || endTime;
            }
        }

        const dayStart = fromZonedTime(
            `${format(selectedDate, 'yyyy-MM-dd')} ${effectiveStartTime}`,
            timeZone
        );

        const finalDayEnd = fromZonedTime(
            `${format(selectedDate, 'yyyy-MM-dd')} ${effectiveEndTime}`,
            timeZone
        );
        const slots = [];
        const now = new Date();

        const lastSlotStartTime = subMinutes(finalDayEnd, slotDifference);

        if (isBefore(lastSlotStartTime, dayStart)) return [];

        const dailySlots = eachMinuteOfInterval(
            { start: dayStart, end: lastSlotStartTime },
            { step: slotDifference }
        );

        // For each slot, determine its status (past, booked, selected, or available)
        dailySlots.forEach(slotStartTime => {
            const isPast = isBefore(slotStartTime, now);
            const isBooked = bookedSlots.some(slotString =>
                parseISO(slotString).getTime() === slotStartTime.getTime()
            )
            // Check if this slot is selected (from any date)
            const isSelected = selectedSlots.some(slot => slot.getTime() === slotStartTime.getTime());

            let status = 'available';
            if (isPast) status = 'past';
            else if (isBooked) status = 'booked';
            else if (isSelected) status = 'selected';

            slots.push({
                time: slotStartTime,
                label: format(slotStartTime, 'h:mm a'),
                status: status
            });
        });

        return slots;

    }, [selectedDate, bookedSlots, selectedSlots, addonSettings, weeklySchedule, holidays, startTime, endTime, slotDifference, timeZone]);



    // --- USER INTERACTION HANDLERS (UPDATED) ---
    const handleTimeSlotClick = (slotTime) => {
        const isSelected = selectedSlots.some(slot => slot.getTime() === slotTime.getTime());

        if (isSelected) {
            setSelectedSlots(currentSlots =>
                currentSlots.filter(slot => slot.getTime() !== slotTime.getTime())
            );
        } else {
            setSelectedSlots(currentSlots => [...currentSlots, slotTime]);
        }
    };


    const bookedEvents = useMemo(() => {
        return bookedSlots.map(slotString => {

            const startDate = parse(slotString, 'yyyy-MM-dd h:mm a', new Date());
            return {
                start: startDate,
                end: new Date(startDate.getTime() + 30 * 60 * 1000),
                display: 'background',
                color: '#ff9f89'
            };
        });
    }, [bookedSlots]);
    // --- FORM CHANGE HANDLER ---
    const handleDetailsChange = (e) => {
        const { name, value } = e.target;
        setPersonalDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // --- FINAL BOOKING HANDLER ---
    const handleFinalBooking = async () => {
        setIsBooking(true);
        setError('');

        // const formattedSlots = selectedSlots.map(slot => ({
        //     datetime: slot.toISOString()
        // }));

        const userTimeZone = getSettingValue('time_zone') || 'UTC';
        // const apiFormat = 'YYYY-MM-DD hh:mm A';
        const apiFormat = 'YYYY-MM-DD HH:mm:ss';

        const formattedSlots = selectedSlots.map(slot => {
            const formattedDatetime = moment(slot).tz(userTimeZone).format(apiFormat);

            return {
                datetime: formattedDatetime
            };
        });

        const payload = {
            people_id: selectedPerson.id,
            subcategory_id: subcategory.value,
            location_id: location.value,
            full_name: personalDetails.fullName,
            phone_number: personalDetails.mobile,
            email: personalDetails.email,
            message: personalDetails.message,
            payment_status: "pending",
            amount: totalAmount,
            // amount: selectedSlots.length,
            appointment_date_time_slots: formattedSlots,
            domain_url: `${window.location.origin}/payment-status`
        };

        try {
            await bookAppointment(payload);

        } catch (err) {
            setError(err.message || 'Could not complete the booking. Please try again.');
            setIsBooking(false);
        }
    };
    // Handler Functions
    const handleNext = () => {
        setCurrentStep(prev => prev + 1);
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };
    // --- USER INTERACTION HANDLERS FOR CALENDAR ---
    const handleDateSelect = (selectionInfo) => {
        // selectionInfo.start
        // selectionInfo.end
        // selectionInfo.allDay
        setSelectedDateTime(selectionInfo);
    };


    const handleSelectAllow = (selectInfo) => {
        return isBefore(new Date(), selectInfo.start);
    };

    // --- VALIDATION ---
    const isStep1Valid = appointmentType && location && category && subcategory;
    const isStep2Valid = selectedPerson;
    const isStep3Valid = selectedSlots.length > 0;

    const isStep4Valid =
        personalDetails.fullName.trim() !== '' &&
        personalDetails.mobile.trim() !== '' &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalDetails.email);

    // Render Logic
    const pricePerSlot = selectedPerson?.appointment_rate || 0;
    const numberOfSlots = selectedSlots.length;
    const totalAmount = pricePerSlot * numberOfSlots;
    if (isLoadingSettings) {
        return (
            <div className="stepper-container">
                <div className="loading-message">
                    <p>Loading Component Settings...</p>
                </div>
            </div>
        );
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="step-content">
                        {/* <h3>Step 1: {steps[1]}</h3> */}
                        <div className="form-group">
                            <label>{addonSettings?.appointment_type_title || 'Appointment Type'}</label>
                            <Select
                                options={appointmentTypeOptions}
                                value={appointmentType}
                                onChange={setAppointmentType}
                                placeholder="Select appointment type..."
                            />
                        </div>
                        <div className="form-group">
                            <label>{addonSettings?.location_title || 'Location'}</label>
                            <Select
                                options={locationOptions}
                                value={location}
                                onChange={setLocation}
                                isDisabled={!appointmentType || isLocationLoading}
                                isLoading={isLocationLoading}
                                placeholder={!appointmentType ? "First select an appointment type" : "Select location..."}
                            />
                        </div>
                        <div className="form-group">
                            <label>{addonSettings?.category_title || 'Category'}</label>
                            <Select
                                options={categoryOptions}
                                value={category}
                                onChange={setCategory}
                                placeholder="Select category..."
                            />
                        </div>
                        <div className="form-group">
                            <label>{addonSettings?.subcategory_title || 'Sub-Category'}</label>
                            <Select
                                options={subcategoryOptions}
                                value={subcategory}
                                onChange={setSubcategory}
                                isDisabled={!category || isSubcategoryLoading}
                                isLoading={isSubcategoryLoading}
                                placeholder={!category ? "First select a category" : "Select sub-category..."}
                            />
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="step-content">
                        {/* <h3>Step 2: {steps[2]}</h3> */}

                        {isPeopleLoading && (
                            <div className="loading-message">

                                <p>Loading People...</p>
                            </div>
                        )}

                        {!isPeopleLoading && peopleData.length === 0 && (
                            <div className="empty-state-message">

                                <p>No people found for the selected criteria.</p>
                            </div>
                        )}

                        {!isPeopleLoading && peopleData.length > 0 && (
                            <div className="people-container">
                                {peopleData.map(person => (
                                    <div
                                        key={person.id}
                                        className={`person-card ${selectedPerson?.id === person.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedPerson(person)}
                                    >
                                        <img
                                            src={person.picture || defaultUserImage}
                                            alt={`${person.first_name} ${person.last_name}`}
                                            className="person-image"
                                        />
                                        <div className="person-details">
                                            <h4>{`${person.title} ${person.first_name} ${person.last_name}`}</h4>

                                            {person.sub_categories && person.sub_categories.length > 0 && (
                                                <p className="person-subcategory">
                                                    {person.sub_categories.map(sc => sc.sub_category_title).join(', ')}
                                                </p>
                                            )}

                                            <p className="person-charges">
                                                {person.currency?.currency_symbol}{person.appointment_rate}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 3:
                // User ki settings (yeh switch statement se pehle define honi chahiye)

                return (
                    <div className="step-content">
                        {isSlotsLoading && <div className="loading-message"><p>Loading Schedule...</p></div>}
                        {!isSlotsLoading && (
                            <div className="date-time-picker-layout">
                                {/* Part 1: FullCalendar (is mein koi change nahi) */}
                                <div className="calendar-container">
                                    <FullCalendar
                                        plugins={[dayGridPlugin, interactionPlugin]}
                                        initialView="dayGridMonth"
                                        headerToolbar={{
                                            left: 'prev',
                                            center: 'title',
                                            right: 'next'
                                        }}
                                        height="auto"
                                        dateClick={handleDateClick}
                                        dayCellClassNames={(arg) => {
                                            const classes = [];
                                            
                                            // Highlight currently viewing date
                                            if (isSameDay(arg.date, selectedDate)) {
                                                classes.push('selected-day-in-month');
                                            }
                                            
                                            // Mark dates that have selected slots
                                            const hasSelectedSlots = selectedSlots.some(slot => 
                                                isSameDay(slot, arg.date)
                                            );
                                            if (hasSelectedSlots && !isSameDay(arg.date, selectedDate)) {
                                                classes.push('has-selected-slots');
                                            }
                                            
                                            // Mark holidays
                                            const dateStr = format(arg.date, 'yyyy-MM-dd');
                                            if (holidays && holidays.length > 0 && holidays.includes(dateStr)) {
                                                classes.push('holiday-date');
                                            }
                                            
                                            // Mark disabled days based on weekly schedule
                                            if (weeklySchedule) {
                                                const dayName = format(arg.date, 'EEEE').toLowerCase();
                                                const daySchedule = weeklySchedule[dayName];
                                                if (!daySchedule || daySchedule.enabled === false) {
                                                    classes.push('disabled-day');
                                                }
                                            }
                                            
                                            return classes;
                                        }}
                                        validRange={{
                                            start: startOfToday(),
                                        }}
                                    />
                                </div>

                                {/* Part 2: Time Slots (yahan tabdeeli ki gayi hai) */}
                                <div className="time-slots-container">
                                    {selectedSlots.length > 0 && (
                                        <div style={{
                                            backgroundColor: '#f0fdf4',
                                            border: '1px solid #86efac',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            marginBottom: '15px'
                                        }}>
                                            <p style={{margin: '0', color: '#065f46', fontWeight: '600', fontSize: '14px'}}>
                                                ✓ {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected
                                                {(() => {
                                                    // Group slots by date
                                                    const dateGroups = {};
                                                    selectedSlots.forEach(slot => {
                                                        const dateKey = format(slot, 'yyyy-MM-dd');
                                                        if (!dateGroups[dateKey]) {
                                                            dateGroups[dateKey] = 0;
                                                        }
                                                        dateGroups[dateKey]++;
                                                    });
                                                    const uniqueDates = Object.keys(dateGroups).length;
                                                    if (uniqueDates > 1) {
                                                        return ` from ${uniqueDates} different dates`;
                                                    }
                                                    return '';
                                                })()}
                                            </p>
                                        </div>
                                    )}
                                    <h4>
                                        Available start times for <strong>{format(selectedDate, 'MMMM d, yyyy')}</strong>
                                    </h4>
                                    <p className="timezone-label text-center">
                                        <TimeZoneLabel timeZone={timeZone} />
                                    </p>
                                    <div className="time-slots-grid">
                                        {calendarEvents.length > 0 ? (
                                            calendarEvents.map(slot => {
                                                const formattedTime = formatDateTimeCustom(
                                                    slot.time,
                                                    userDateFormat,
                                                    userTimeFormat,
                                                    timeZone,
                                                    false
                                                );
                                                return (
                                                    <button
                                                        key={slot.time}
                                                        className={`time-slot-btn ${slot.status}`}
                                                        disabled={slot.status === 'past' || slot.status === 'booked'}
                                                        onClick={() => handleTimeSlotClick(slot.time)}
                                                    >
                                                        {formattedTime}
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            (() => {
                                                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                                                const dayName = format(selectedDate, 'EEEE').toLowerCase();
                                                const daySchedule = weeklySchedule ? weeklySchedule[dayName] : null;
                                                const isHoliday = holidays && holidays.includes(dateStr);
                                                const isDisabled = weeklySchedule && (!daySchedule || daySchedule.enabled === false);
                                                
                                                if (isHoliday) {
                                                    return <p className="no-slots-message" style={{color: '#dc2626', fontWeight: '600'}}>This date is a holiday. Please select another date.</p>;
                                                } else if (isDisabled) {
                                                    return <p className="no-slots-message" style={{color: '#9ca3af', fontWeight: '600'}}>This day is not available. Please select another date.</p>;
                                                } else {
                                                    return <p className="no-slots-message">No available slots for this day.</p>;
                                                }
                                            })()
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 4:
                return (
                    <div className="step-content">
                        {/* <h3>Step 4: {steps[4]}</h3> */}
                        <div className="personal-details-form">
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name *</label>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid' id="fullName" name="fullName" value={personalDetails.fullName} onChange={handleDetailsChange} placeholder="Enter your full name" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    className='form-control form-control-lg form-control-solid' type="email" id="email" name="email" value={personalDetails.email} onChange={handleDetailsChange} placeholder="Enter your email" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="mobile">Mobile Number *</label>
                                <input
                                    className='form-control form-control-lg form-control-solid' type="tel" id="mobile" name="mobile" value={personalDetails.mobile} onChange={handleDetailsChange} placeholder="Enter your mobile number" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="message">Message (Optional)</label>
                                <textarea
                                    className='form-control form-control-lg form-control-solid' id="message" name="message" value={personalDetails.message} onChange={handleDetailsChange} rows="4" placeholder="Any additional information..."></textarea>
                            </div>
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className="step-content">
                        {/* <h3>Step 5: {steps[5]}</h3> */}
                        <div className="summary-container">
                            <div className="summary-section">
                                <h4>Appointment Details</h4>
                                <table className="summary-table">
                                    <tbody>
                                        <tr>
                                            <td>Appointment With:</td>
                                            <td>{selectedPerson?.title} {selectedPerson?.first_name} {selectedPerson?.last_name}</td>
                                        </tr>
                                        <tr>
                                            <td>Service:</td>
                                            <td>{subcategory?.label}</td>
                                        </tr>
                                        <tr>
                                            <td>Location / Mode:</td>
                                            <td>{location?.label} ({appointmentType?.label})</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="summary-section">
                                <h4>Your Details</h4>
                                <table className="summary-table">
                                    <tbody>
                                        <tr>
                                            <td>Full Name:</td>
                                            <td>{personalDetails.fullName}</td>
                                        </tr>
                                        <tr>
                                            <td>Email:</td>
                                            <td>{personalDetails.email}</td>
                                        </tr>
                                        <tr>
                                            <td>Mobile:</td>
                                            <td>{personalDetails.mobile}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="summary-section">
                                <h4>Selected Slots</h4>
                                <ul className="slots-summary-list">
                                    {selectedSlots.sort((a, b) => a - b).map(slot => {
                                        const slotISO = slot.toISOString();
                                        const formattedDate = formatDynamicDateOnly(
                                            slotISO,
                                            userDateFormat,
                                            timeZone
                                        );
                                        const formattedTime = formatDateTimeCustom(
                                            slotISO,
                                            userDateFormat,
                                            userTimeFormat,
                                            timeZone,
                                            false
                                        );
                                        return (
                                            <li key={slotISO}>
                                                {formattedDate} at <strong>{formattedTime}</strong>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div className="summary-section payment-summary">
                                <h4>Payment Summary</h4>
                                <table className="summary-table">
                                    <tbody>
                                        <tr>
                                            <td>Price per Slot:</td>
                                            <td>{selectedPerson?.currency?.currency_symbol}{pricePerSlot}</td>
                                        </tr>
                                        <tr>
                                            <td>Number of Slots:</td>
                                            <td>x {numberOfSlots}</td>
                                        </tr>
                                        <tr className="total-row">
                                            <td>Total Amount:</td>
                                            <td>{selectedPerson?.currency?.currency_symbol}{totalAmount}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            default:
                return <div>Unknown Step</div>;
        }
    };

    return (
        <div className="stepper-container">
            {/* Stepper Header */}
            <div className="stepper-header">
                {Object.keys(steps).map(step => (
                    <div key={step} className={`step-item ${currentStep >= step ? 'completed' : ''}`}>
                        <div className="step-counter">{step}</div>
                        <div className="step-name">{steps[step]}</div>
                    </div>
                ))}
            </div>

            {/* Step Content */}
            <div className="stepper-body">
                {error && <p className="error-message">{error}</p>}
                <h3 className='text-center'>{addonSettings?.form_title + ' with ' + addonSettings?.organization_name || 'Book an Appointment'}</h3>
                {renderStepContent()}
            </div>

            {/* Stepper Footer/Controls */}
            <div className="stepper-footer">
                {currentStep > 1 && (
                    <button className="stepper_button_01032024" onClick={handleBack} disabled={isBooking}>
                        Previous
                    </button>
                )}
                {currentStep === 5 ? (
                    <button
                        className="confirm_pay_01032024"
                        onClick={handleFinalBooking}
                        disabled={isBooking}
                    >
                        {isBooking ? 'Processing...' : 'Confirm & Pay'}
                    </button>
                ) :


                    currentStep < 6 && (
                        <button
                            className={`next_button_01032024 ${(currentStep === 1 && !isStep1Valid) ||
                                (currentStep === 2 && !isStep2Valid) ||
                                (currentStep === 3 && !isStep3Valid) ||
                                (currentStep === 4 && !isStep4Valid)
                                ? 'disabled_button_01032024'
                                : 'stepper_button_01032024'
                                }`}
                            onClick={handleNext}
                            disabled={
                                (currentStep === 1 && !isStep1Valid) ||
                                (currentStep === 2 && !isStep2Valid) ||
                                (currentStep === 3 && !isStep3Valid) ||
                                (currentStep === 4 && !isStep4Valid)
                            }
                        >
                            Next
                        </button>
                    )}
            </div>
        </div>
    );
};

export default AppointmentStepper;